import {
  type IndexData,
  indexDataValue,
  type PensionEstimate,
  pensionEstimateValue,
} from "../types/Codes";
import type { OveModel, OveResult } from "../types/OveModel";

export interface OveCalculationContext {
  readonly calculationYear: number;
  readonly calculationMonth: number;
  readonly indexData?: IndexData[];
  readonly pensionEstimate?: PensionEstimate[];
}

export function calculateOve(
  model: OveModel,
  context: OveCalculationContext,
): OveResult {
  let exceptionalLifetimeFactor;
  let exceptionalLifetimeFactorUsed = model.isExceptionalLifetimeFactorUsed;
  if (
    (model.birthYear < 1964 && model.oveAgeYear == 0) ||
    model.oveAgeYear == 61
  ) {
    exceptionalLifetimeFactorUsed = true;
    const haetutVEikaJaEK = haeAlinVanhuuselakeIkaJaElinaikakerroin(
      model.birthYear - 1,
      context.pensionEstimate,
    );
    exceptionalLifetimeFactor = haetutVEikaJaEK.ek;
  }

  /* Haetaan alin vanhuuseläkeikä ja elinaikakerroin */
  const haetutVEikaJaEK = haeAlinVanhuuselakeIkaJaElinaikakerroin(
    model.birthYear,
    context.pensionEstimate,
  );

  const lowestPensionAgeYear = haetutVEikaJaEK.lowestPensionAgeYear;
  const lowestPensionAgeMonth = haetutVEikaJaEK.lowestPensionAgeMonth;
  const lifetimeFactor = haetutVEikaJaEK.ek;
  const lifetimeFactorFormatted = haetutVEikaJaEK.ekFormatted;

  let pensionAgeYear = model.pensionAgeYear;
  let pensionAgeMonth = model.pensionAgeMonth;
  /* Jos aluksi ei annettu valittua ikää */
  if (model.pensionAgeYear === 0) {
    pensionAgeYear = lowestPensionAgeYear;
    pensionAgeMonth = lowestPensionAgeMonth;
  }

  /* Tarkistetaan vielä, että valittu vanhuuseläkeikä on vähintään alin vanhuuseläkeikä */
  if (
    !tarkistaValittuIka(
      pensionAgeYear,
      pensionAgeMonth,
      lowestPensionAgeYear,
      lowestPensionAgeMonth,
    )
  ) {
    pensionAgeYear = lowestPensionAgeYear;
    pensionAgeMonth = lowestPensionAgeMonth;
  }

  /* Apumuuttujia */
  let palkkaMuuttumaton = model.income;
  let income = model.income;
  let accruedPension = model.accruedPension;
  let incomeWithOve = model.incomeWithOve;

  /* Laskenta-ajankohta */
  /* 17.5.2023 TYEL-395 kuukausi 0-11 eli edellinen täysi kuukausi, mutta muutetaan 0->12 ja tällöin myös vuosi yhtä aiempi */
  let laskentaVuosi = context.calculationYear;
  let laskentaKuukausi = context.calculationMonth;
  if (laskentaKuukausi == 0) {
    laskentaKuukausi = 12;
    laskentaVuosi = laskentaVuosi - 1;
  }

  /* 17.5.2023 TYEL-395 tarkemmin vuosiloopin aloitus */
  let alkuVuosi = laskentaVuosi;
  if (laskentaKuukausi === 12) {
    alkuVuosi = laskentaVuosi + 1;
  }

  /* Tarkistetaan ensin valittu eläkkeellejäämisikä */
  let elakeIkaTarkistus = tarkistaElakeIka(
    laskentaVuosi,
    laskentaKuukausi,
    model.birthYear,
    model.birthMonth,
    pensionAgeYear,
    pensionAgeMonth,
  );

  if (elakeIkaTarkistus.ylittaaElakeikansa) {
    pensionAgeYear = elakeIkaTarkistus.valittuAlvev;
    pensionAgeMonth = elakeIkaTarkistus.valittuAlvekk;
  }

  /* Tarkistetaan sitten OVE-ikä */

  const oveElakeIkaTarkistus = oveIkaPaattely(
    laskentaVuosi,
    laskentaKuukausi,
    model.birthYear,
    model.birthMonth,
    lowestPensionAgeYear,
    lowestPensionAgeMonth,
    model.oveAgeYear,
    model.oveAgeMonth,
  );
  /* Funktio palauttaa lasketun OVE-iän sekä OVE-iän jolla voi jäädä OVE:lle */
  const calculatedOveAgeYear = oveElakeIkaTarkistus.calculatedOveAgeYear;
  const calculatedOveAgeMonth = oveElakeIkaTarkistus.calculatedOveAgeMonth;

  let oveAgeYear = oveElakeIkaTarkistus.oveAgeYear;
  let oveAgeMonth = oveElakeIkaTarkistus.oveAgeMonth;

  /* Tarkistetaan vielä erikseen, pitäisikö käyttää poikkeus EK:ta */
  if (oveAgeYear > 61 && exceptionalLifetimeFactorUsed) {
    exceptionalLifetimeFactorUsed = false;
  }

  /* Tarkistetaan vielä, että henkilö ei ylitä annettua OVE-ikää */
  const elakeIkaTarkistusUudestaan = tarkistaElakeIka(
    laskentaVuosi,
    laskentaKuukausi,
    model.birthYear,
    model.birthMonth,
    oveAgeYear,
    oveAgeMonth,
  );

  if (elakeIkaTarkistusUudestaan.ylittaaElakeikansa) {
    oveAgeYear = elakeIkaTarkistusUudestaan.valittuAlvev;
    oveAgeMonth = elakeIkaTarkistusUudestaan.valittuAlvekk;
  }

  /* Jos henkilö ei jatka OVE:lla työskentelyä, laitetaan OVE:n aikainen palkka nollaan */
  if (!model.continuesWorking) {
    incomeWithOve = 0;
  }

  /* Varsinaisen VE-eläkkeen eläkehetki */
  const elakeHetki = elakeVuosiJaKk(
    pensionAgeYear,
    pensionAgeMonth,
    model.birthYear,
    model.birthMonth,
  );
  // 17.5.2023
  let pensionYear = elakeHetki.elakeVuosi;
  let pensionMonth = elakeHetki.elakeKuukausi;
  let poikkeusIndeksointi = elakeHetki.poikkeusIndeksointi;

  // karttumien laskennallinen eläkehetki
  const elakeHetkiOVE = elakeVuosiJaKk(
    oveAgeYear,
    oveAgeMonth,
    model.birthYear,
    model.birthMonth,
  );
  // 17.5.2023
  // THESE ARE NOT USED IN THE ORIGINAL!
  //const elakeVuosiOVE = elakeHetkiOVE.elakeVuosi - 1;
  //const elakeKuukausiOVE = 12;

  // todellinen ove eläkehetki
  //const elakeHetkiOVEtodellinen = elakeVuosiJaKk(oveAgeYear, oveAgeMonth, model);
  // THE RESULT IS NOT USED IN THE ORIGINAL CODE!
  // 17.5.2023
  let elakeVuosiOVEtodellinen = elakeHetkiOVE.elakeVuosi;
  const elakeKuukausiOVEtodellinen = elakeHetkiOVE.elakeKuukausi;
  const poikkeusIndeksointiOVE = elakeHetkiOVE.poikkeusIndeksointi;

  /* tarkistetaan, voiko hlö jäädä ovelle, eli että vanhuuseläkeikä on suurempi kuin ove-ikä */
  let onkoVEsuurempiKuinOVEika = vertaaIkia(
    pensionAgeYear,
    pensionAgeMonth,
    oveAgeYear,
    oveAgeMonth,
  );

  if (!onkoVEsuurempiKuinOVEika) {
    if (pensionAgeMonth == 12) {
      pensionAgeMonth = 0;
      pensionAgeYear = oveAgeYear + 1;
    } else {
      pensionAgeMonth = pensionAgeMonth + 1;
    }

    const elakeHetki = elakeVuosiJaKk(
      pensionAgeYear,
      pensionAgeMonth,
      model.birthYear,
      model.birthMonth,
    );
    pensionYear = elakeHetki.elakeVuosi;
    pensionMonth = elakeHetki.elakeKuukausi;
    poikkeusIndeksointi = elakeHetki.poikkeusIndeksointi;

    onkoVEsuurempiKuinOVEika = true;
  }

  // muunnetaan kertynyt eläke muotoon, jossa ei ole huomioitu elinaikakerrointa
  accruedPension = accruedPension / lifetimeFactor;

  // tähän tulee OVE:n aikaisen eläkkeen kertymät vanhalla palkalla
  let kertynytElakeOVEvanha = 0;

  // tähän tulee OVEn laskennassa käytetty karttuma OVEllejääntihetkellä
  // sekä korkoutettu oven laskennassa käytetty karttuma
  let OVEkarttuma = 0;
  let OVEkarttumaLahto = 0;

  // varhennusvähennyksen suuruus
  const OVEvarhennusvahennys =
    (lowestPensionAgeYear - oveAgeYear) * 12 +
    (lowestPensionAgeMonth - oveAgeMonth);

  // OVE:n varhennusvähennyksen määrä euroissa elinaikakertoimella kerrottuna tulee tähän
  let OVEvarhennusvahennysEUR = 0;

  // ja tähän itse OVE
  // OVE jota korkoutetaan sekä OVE saantihetkellä
  let OVEelake = 0;
  let OVEelakeLahto = 0;

  /* Tähän lasketaan vanhuuseläke ilman OVE:a */
  let pensionWithoutOve = accruedPension;

  /* Onko henkilö saanut OVE:n kuluvana vuonna */
  let onSaanutOVEn = false;

  let palkkakerroinTulevaKorotus;
  let OVEelakeTarkistettu = 0;

  /* diskonttausta letten vakiot */
  let alkuHintataso = 1;
  let loppuHintataso = 1;

  // 17.5.2023 TYEL-395 ikä edellisenä vuonna
  let ika = alkuVuosi - model.birthYear - 1;

  // New initializations
  let poikkeusPalkkakerroin = 0.0;

  /* Eläkeikään pitää vielä päivittää vuodet */

  if (pensionYear < alkuVuosi) {
    // 17.5.2023 TYEL-395
    pensionYear = alkuVuosi;
  }

  if (elakeVuosiOVEtodellinen < alkuVuosi) {
    // 17.5.2023 TYEL-395
    elakeVuosiOVEtodellinen = alkuVuosi;
  }

  /* 17.5.2023 TYEL-395 korvataan laskennassa vuosi + 1 -> alkuVuosi */
  for (let i = alkuVuosi; i <= pensionYear; ++i) {
    let palkkaTemp = 0;

    ika = ika + 1;

    // TYEL-442
    //if (parseInt(value.Vuosi) === i) {
    let palkkakerroin =
      indexDataValue(i, "Palkkakerroin", context.indexData) || 1;
    // NOT USED IN THE ORIGINAL:
    // let hintaTaso = indexDataValue(i, "Hintataso", context.indexData) || 1;
    // NOT REALLY USED, used only if palkankasvu was other than '2', but it is always '2'
    // let ansiotaso = indexDataValue(i, "Ansiotaso", context.indexData) || 1;

    //tyoelakeindeksi = 0.2 * ansiotaso + 0.8 * hintataso;
    let tyoelakeindeksi = indexDataValue(i, "TEI", context.indexData) || 1;

    /* OVE:lle jäämisen hetken indeksi */
    let KHIOve = indexDataValue(i, "KHI", context.indexData) || 1;
    let teindeksiSeurVuosi =
      indexDataValue(i + 1, "TEI", context.indexData) || 1;

    const KHIedVuosi = indexDataValue(i - 1, "KHI", context.indexData) || 1;

    if (i === alkuVuosi) {
      // 17.5.2023 TYEL-395
      alkuHintataso = KHIOve;
    }

    if (i === pensionYear && !poikkeusIndeksointi) {
      loppuHintataso = KHIOve;
    }
    if (poikkeusIndeksointi) {
      loppuHintataso = indexDataValue(i + 1, "KHI", context.indexData) || 1;
      poikkeusPalkkakerroin =
        indexDataValue(i + 1, "Palkkakerroin", context.indexData) || 1;
    }

    palkkakerroinTulevaKorotus = palkkakerroin;

    if (model.incomeDevelopment === "static") {
      palkkakerroin = KHIOve / KHIedVuosi;
      // teindeksiSeurVuosi = KHISeurVuosi / KHIOve;
    }

    let karttumaprosentti = karttumaprosenttiPoikkeukset(
      model.birthYear,
      model.birthMonth,
      pensionAgeYear,
      pensionAgeMonth,
      i,
    );

    // oletus, että OVEn jälkeen alkava palkka on ilmoitettu laskentavuoden tasossa, eli korkoutetaan
    // sitä muun palkan mukana
    if (i !== alkuVuosi) {
      income = income * palkkakerroin;
      palkkaMuuttumaton = palkkaMuuttumaton * palkkakerroin;
      incomeWithOve = incomeWithOve * palkkakerroin;
    }

    let uusi_karttuma = 0;
    let uusi_karttuma_muuttumatonpalkka = 0;

    let karttumakuukaudet = karttumaKuukaudet(
      i,
      pensionYear,
      pensionAgeYear,
      pensionAgeMonth,
      model.birthYear,
      model.birthMonth,
      ika,
      alkuVuosi,
      laskentaKuukausi,
    );
    // 17.5.2023 TYEL-395

    /* Tarkistetaan pitääkö palkkaa muuttaa OVE:n jälkeen */

    if (onkoVEsuurempiKuinOVEika) {
      if (elakeVuosiOVEtodellinen == i) {
        let kuukaudetUusiPalkka =
          karttumakuukaudet - elakeKuukausiOVEtodellinen + 1;
        /* 17.5.2023 TYEL-395 alkuvuonna kuluneet kuukaudet aiemmin on otettu karttumakuukausista pois, nyt palautetaan tähän laskentaan */
        if (i === alkuVuosi && laskentaKuukausi < 12) {
          kuukaudetUusiPalkka = kuukaudetUusiPalkka + laskentaKuukausi;
        }
        let kuukaudetVanhaPalkka = karttumakuukaudet - kuukaudetUusiPalkka;

        if (karttumakuukaudet > 0) {
          palkkaTemp = income;
          income =
            income * (kuukaudetVanhaPalkka / karttumakuukaudet) +
            incomeWithOve * (kuukaudetUusiPalkka / karttumakuukaudet);
        }
      } else if (i == elakeVuosiOVEtodellinen + 1) {
        income = incomeWithOve;
      }
    }

    // OVE:n täyttämisvuonna otetaan talteen kuinka paljon eläkekertymä kun siirrytään OVE:lle,
    // ja muunnetaan palkka OVEpalkaksi
    // erikoistapauksena huomioidaan tilanne, jossa hlö:n laskennallinen OVE-hetki
    // on heti laskennan alussa
    // tarkistetaan myös, että vanhuuseläkkeellejäämishetki on aidosti myöhemmin kuin OVElle jäämishetki

    if (elakeVuosiOVEtodellinen === i && onkoVEsuurempiKuinOVEika) {
      /* OVE:n laskennassa ei huomioida kuluvan vuoden karttumia */
      /* OVE:n laskennassa korotetaan kuitenkin 2026 tasoon */

      /* Tässä oletus, että annettu karttuma on jo korotettu seuraavan vuoden tasoon */
      /* 17.5.2023 TYEL-395 syötetty karttuma on edellisen kuukauden tasossa, joten OVE:n laskentaa varten lasketaan alkuvuoden toteutunut karttuma */
      let alkuVuodenKarttumaTot = 0;

      if (alkuVuosi === elakeVuosiOVEtodellinen) {
        if (laskentaKuukausi < 12) {
          alkuVuodenKarttumaTot =
            (palkkaTemp * karttumaprosentti * laskentaKuukausi) / 12;
          alkuVuodenKarttumaTot = Math.min(
            alkuVuodenKarttumaTot,
            accruedPension,
          );
        }
        OVEkarttuma = accruedPension - alkuVuodenKarttumaTot;
      } else {
        if (i === alkuVuosi + 1) {
          OVEkarttuma = accruedPension * palkkakerroinTulevaKorotus;
        } else {
          OVEkarttuma = accruedPension * palkkakerroin;
        }
      }

      OVEkarttumaLahto =
        (1 - model.ovePercentage) * OVEkarttuma + alkuVuodenKarttumaTot;

      if (exceptionalLifetimeFactorUsed && exceptionalLifetimeFactor) {
        OVEvarhennusvahennysEUR =
          OVEvarhennusvahennys *
          0.004 *
          OVEkarttuma *
          model.ovePercentage *
          exceptionalLifetimeFactor;
        OVEelake =
          OVEkarttuma *
          (1 - OVEvarhennusvahennys * 0.004) *
          model.ovePercentage *
          exceptionalLifetimeFactor;
      } else {
        OVEvarhennusvahennysEUR =
          OVEvarhennusvahennys *
          0.004 *
          OVEkarttuma *
          model.ovePercentage *
          lifetimeFactor;
        OVEelake =
          OVEkarttuma *
          (1 - OVEvarhennusvahennys * 0.004) *
          model.ovePercentage *
          lifetimeFactor;
      }

      OVEelakeLahto = OVEelake;

      /* Muunnetaan OVE-eläke tämän vuoden hintatasolla */
      // TYEL-442
      let muuntoKerroinOVE = KHIOve / alkuHintataso;
      if (model.priceConversion) {
        OVEelakeLahto = OVEelakeLahto / muuntoKerroinOVE;
        OVEvarhennusvahennysEUR = OVEvarhennusvahennysEUR / muuntoKerroinOVE;
      }

      if (elakeVuosiOVEtodellinen == i) {
        OVEelakeTarkistettu = OVEelakeLahto * teindeksiSeurVuosi;
      }

      // tähän kerääntyy OVE:n jälkeen kartutettu eläke
      uusi_karttuma = income * karttumaprosentti * (karttumakuukaudet / 12);
      accruedPension = 0;
      kertynytElakeOVEvanha = 0;

      /* Hlö saanut OVE:n */
      onSaanutOVEn = true;
    } else {
      uusi_karttuma = income * karttumaprosentti * (karttumakuukaudet / 12);
    }

    uusi_karttuma_muuttumatonpalkka =
      palkkaMuuttumaton * karttumaprosentti * (karttumakuukaudet / 12);

    /* Korkoutetaan karttumat */
    if (i === alkuVuosi) {
      // 17.5.2023 TYEL-395
      accruedPension = accruedPension + uusi_karttuma;

      kertynytElakeOVEvanha =
        kertynytElakeOVEvanha + uusi_karttuma_muuttumatonpalkka;

      pensionWithoutOve = pensionWithoutOve + uusi_karttuma_muuttumatonpalkka;

      if (onSaanutOVEn) {
        onSaanutOVEn = false;
      }
    } else if (i === alkuVuosi + 1) {
      accruedPension =
        accruedPension * palkkakerroinTulevaKorotus + uusi_karttuma;

      kertynytElakeOVEvanha =
        kertynytElakeOVEvanha * palkkakerroinTulevaKorotus +
        uusi_karttuma_muuttumatonpalkka;

      if (onSaanutOVEn) {
        /* On jo korkoutettu OVE:n laskennassa poikkeuksellisesti */
        onSaanutOVEn = false;
      } else {
        OVEelake = OVEelake * tyoelakeindeksi;
        OVEkarttumaLahto = OVEkarttumaLahto * palkkakerroinTulevaKorotus;
      }

      pensionWithoutOve =
        pensionWithoutOve * palkkakerroinTulevaKorotus +
        uusi_karttuma_muuttumatonpalkka;
    } else {
      accruedPension = accruedPension * palkkakerroin + uusi_karttuma;

      kertynytElakeOVEvanha =
        kertynytElakeOVEvanha * palkkakerroin + uusi_karttuma_muuttumatonpalkka;

      if (onSaanutOVEn) {
        /* On jo korkoutettu OVE:n laskennassa poikkeuksellisesti */
        onSaanutOVEn = false;
      } else {
        OVEelake = OVEelake * tyoelakeindeksi;
        OVEkarttumaLahto = OVEkarttumaLahto * palkkakerroin;
      }

      pensionWithoutOve =
        pensionWithoutOve * palkkakerroin + uusi_karttuma_muuttumatonpalkka;
    }

    // Tässä palautetaan palkka, jos palkkaa muutettu vuoden aikana OVE:n siirtymävuotta varten
    if (palkkaTemp > 0) {
      income = palkkaTemp;
    }
  }

  if (poikkeusIndeksointi) {
    income = income * poikkeusPalkkakerroin;
    accruedPension = accruedPension * poikkeusPalkkakerroin;
    kertynytElakeOVEvanha = kertynytElakeOVEvanha * poikkeusPalkkakerroin;
    pensionWithoutOve = pensionWithoutOve * poikkeusPalkkakerroin;
  }

  //lykkäyskorotus ja loput karttumat alimman vanhuuseläkeiän jälkeen
  let lykkayskorotus = lykkaysKorotusKuukaudet(
    model.birthYear,
    model.birthMonth,
    pensionAgeYear,
    pensionAgeMonth,
    lowestPensionAgeYear,
    lowestPensionAgeMonth,
  );
  // THIS WAS NOT USED IN ORIGINAL
  //let lykkayskorotusOVE = lykkaysKorotusKuukaudet(sv, sk, oveAgeYear, oveAgeMonth, pensionYear, lowestPensionAgeMonth);

  let elakeIlmanLykkayskorotusta =
    (accruedPension + OVEkarttumaLahto) * lifetimeFactor + OVEelake;
  // Needed in calculation Kela OVE
  let elakeIlmanLykkayskorotustaMuuttumatonPalkka = pensionWithoutOve;

  /* Huomioidaan lykkäyskorotukset */
  // tähän talteen lykkäyskorotuksen vaikutus ve-eläkkeeseen
  let lykkayskorotusVE =
    (accruedPension + OVEkarttumaLahto) *
    (lykkayskorotus * 0.004) *
    lifetimeFactor;

  accruedPension = accruedPension * (1 + lykkayskorotus * 0.004);
  OVEkarttumaLahto = OVEkarttumaLahto * (1 + lykkayskorotus * 0.004);

  pensionWithoutOve = pensionWithoutOve * (1 + lykkayskorotus * 0.004);

  /* Alkuperäiseen OVE:n eri lykkäyskorotus, eli lykkäyskorotus vanhuuseläkeiästä OVE-ikään */
  //OVEelake = OVEelake * (1 + lykkayskorotusOVE * 0.004);

  pensionWithoutOve = pensionWithoutOve * lifetimeFactor;
  elakeIlmanLykkayskorotustaMuuttumatonPalkka =
    elakeIlmanLykkayskorotustaMuuttumatonPalkka * lifetimeFactor;

  /* Palkan muutoksen vaikutus vanhuuseläkkeeseen */
  /* kertynytElake sisältää OVE:n aikaiset karttumat OVE:n aikaisella palkalla */
  let palkkaVaikutusElakkeeseen =
    (kertynytElakeOVEvanha - accruedPension) * lifetimeFactor;

  // Lasketaan OVE:n jälkeinen VE
  accruedPension =
    accruedPension * lifetimeFactor +
    OVEelake +
    OVEkarttumaLahto * lifetimeFactor;

  /* Kelan eläkettä varten kertynyt eläke ilman varhennusvähennystä */
  let OVEelakeVarhennusvahennys =
    OVEelake / (1 - OVEvarhennusvahennys * 0.004) - OVEelake;
  let kertynytElakeIlmanvarhennusvahennystaJaIlmanLykkayskorotusta =
    elakeIlmanLykkayskorotusta + OVEelakeVarhennusvahennys;

  let OVEelakeVarhennusvahennusTuloksiin = OVEelakeVarhennusvahennys;

  // TYEL-442
  const priceLevelIncrease = loppuHintataso / alkuHintataso;

  //Muunnetaan vielä suureet vuoden 2019 tasoon
  if (alkuVuosi === pensionYear) {
    // 17.5.2023 TYEL-395
    //jos jää samana vuonna eläkkeelle, nyt ei tehdä mitään, eläkekarttuma on korotettu oikeaan tasoon
  } else if (!model.priceConversion) {
    //valittuna nimellinen hintataso eli ei diskonttausta
  } else {
    accruedPension = accruedPension / priceLevelIncrease;
    lykkayskorotusVE = lykkayskorotusVE / priceLevelIncrease;
    income = income / priceLevelIncrease;
    // WAS NOT USED IN THE ORIGINAL
    //elakeIlmanLykkayskorotusta = elakeIlmanLykkayskorotusta / hintaTasonKasvu;

    pensionWithoutOve = pensionWithoutOve / priceLevelIncrease;
    elakeIlmanLykkayskorotustaMuuttumatonPalkka =
      elakeIlmanLykkayskorotustaMuuttumatonPalkka / priceLevelIncrease;
    kertynytElakeIlmanvarhennusvahennystaJaIlmanLykkayskorotusta =
      kertynytElakeIlmanvarhennusvahennystaJaIlmanLykkayskorotusta /
      priceLevelIncrease;

    palkkaVaikutusElakkeeseen = palkkaVaikutusElakkeeseen / priceLevelIncrease;
    OVEelakeVarhennusvahennusTuloksiin =
      OVEelakeVarhennusvahennusTuloksiin / priceLevelIncrease;
  }

  if (poikkeusIndeksointi) {
    pensionYear = pensionYear + 1;
  }

  if (poikkeusIndeksointiOVE) {
    elakeVuosiOVEtodellinen = elakeVuosiOVEtodellinen + 1;
  }

  return {
    lowestPensionAgeYear: lowestPensionAgeYear,
    lowestPensionAgeMonth: lowestPensionAgeMonth,
    lifetimeFactor: lifetimeFactorFormatted,
    income,
    pensionYear,
    pensionMonth,
    elakeVuosiOVEtodellinen,
    elakeKuukausiOVEtodellinen,
    ovePension: OVEelakeLahto,
    pensionWithOve: accruedPension,
    pensionWithoutOve: pensionWithoutOve,
    pensionAgeYear,
    pensionAgeMonth,
    oveAgeYear,
    oveAgeMonth,
    calculatedOveAgeYear: calculatedOveAgeYear,
    calculatedOveAgeMonth: calculatedOveAgeMonth,
    OVEvarhennusAika: OVEvarhennusvahennys,
    OVEvarhennusVahennys: (OVEvarhennusvahennys * 0.004).toFixed(4),
    OVEelakeVarhennusvahennys: OVEelakeVarhennusvahennusTuloksiin,
    OVEpalkkaVaikutusElakkeeseen: palkkaVaikutusElakkeeseen,
    OVEvarhennusvahennysEUR,
    lykkayskorotusVE,
    OVEelakeSeurVuosi: OVEelakeTarkistettu,
    kertynytElakeIlmanvarhennusvahennystaJaIlmanLykkayskorotusta,
    elakeIlmanLykkayskorotustaMuuttumatonPalkka,
    priceLevelIncrease,
  };
}

interface ElakeIkaResult {
  ylittaaElakeikansa: boolean;
  valittuAlvev: number;
  valittuAlvekk: number;
}

interface IkaPaattelyResult {
  calculatedOveAgeYear: number;
  calculatedOveAgeMonth: number;
  oveAgeYear: number;
  oveAgeMonth: number;
}

interface ElakeVuosiJaKkResult {
  elakeVuosi: number;
  elakeKuukausi: number;
  poikkeusIndeksointi: boolean;
}

function tarkistaElakeIka(
  laskentaVuosi: number,
  laskentaKuukausi: number,
  sv: number,
  sk: number,
  lowestPensionAgeYear: number,
  lowestPensionAgeMonth: number,
): ElakeIkaResult {
  let valittuAlvev = lowestPensionAgeYear;
  let valittuAlvekk = lowestPensionAgeMonth;
  let ylittaaElakeikansa = false;
  /* Jos henkilö ylittää eläkeikänsä, päivitetään valittua VE_ikää 7.6.2023 TYEL-395*/
  if (
    (laskentaVuosi - sv) * 12 + laskentaKuukausi - sk >
    lowestPensionAgeYear * 12 + lowestPensionAgeMonth
  ) {
    // 17.5.2023 TYEL-395
    valittuAlvev = laskentaVuosi - sv;
    valittuAlvekk = laskentaKuukausi - sk;
    /* Korjaus 24.5.2023 TYEL-395 */
    if (valittuAlvekk < 0) {
      valittuAlvekk = valittuAlvekk + 12;
      valittuAlvev = valittuAlvev - 1;
    }

    ylittaaElakeikansa = true;
  }

  return { ylittaaElakeikansa, valittuAlvev, valittuAlvekk };
}

function oveIkaPaattely(
  laskentaVuosi: number,
  laskentaKuukausi: number,
  birthYear: number,
  birthMonth: number,
  lowestPensionAgeYear: number,
  lowestPensionAgeMonth: number,
  givenOveAgeYear: number,
  givenOveAgeMonth: number,
): IkaPaattelyResult {
  let veIkaNew: number;
  let vekkNew: number;

  /* ensin laskennallinen OVE-ikä */
  if (birthYear < 1964) {
    veIkaNew = 61;
    vekkNew = 0;
  } else {
    veIkaNew = lowestPensionAgeYear - 3;
    vekkNew = lowestPensionAgeMonth;
  }

  /* Jos annettu oveikä 0 tai virheellinen oveikä, lasketaan ove-ikä uudelleen */
  if (
    givenOveAgeYear === 0 ||
    !tarkistaOveIka(
      givenOveAgeYear,
      givenOveAgeMonth,
      lowestPensionAgeYear,
      lowestPensionAgeMonth,
      birthYear,
    )
  ) {
    let korjattuVeIka;
    let korjattuVeKk;

    /* Tarkistetaan vielä, mikä OVE ikä pitää oikeasti olla */
    let tarkistettuOVEika = tarkistaElakeIka(
      laskentaVuosi,
      laskentaKuukausi,
      birthYear,
      birthMonth,
      veIkaNew,
      vekkNew,
    );

    if (tarkistettuOVEika.ylittaaElakeikansa) {
      korjattuVeIka = tarkistettuOVEika.valittuAlvev;
      korjattuVeKk = tarkistettuOVEika.valittuAlvekk;
    } else {
      korjattuVeIka = veIkaNew;
      korjattuVeKk = vekkNew;
    }
    return {
      calculatedOveAgeYear: veIkaNew,
      calculatedOveAgeMonth: vekkNew,
      oveAgeYear: korjattuVeIka,
      oveAgeMonth: korjattuVeKk,
    };
    /* Muuten palautetaan laskettu OVE-ikä ja alkuperäiset arvot */
  } else {
    return {
      calculatedOveAgeYear: veIkaNew,
      calculatedOveAgeMonth: vekkNew,
      oveAgeYear: givenOveAgeYear,
      oveAgeMonth: givenOveAgeMonth,
    };
  }
}

function tarkistaOveIka(
  oveAgeYear: number,
  oveAgeMonth: number,
  lowestPensionAgeYear: number,
  lowestPensionAgeMonth: number,
  sv: number,
): boolean {
  if (sv <= 1964) {
    return true;
  }

  const oveMonth = oveAgeYear * 12 + oveAgeMonth;
  const pensionMonth = lowestPensionAgeYear * 12 + lowestPensionAgeMonth;

  /* Tarkistetaan, että OVEa yritetään ottaa korkeintaan 3v ennen eläkeikää */
  return pensionMonth - oveMonth <= 12 * 3;
}

function elakeVuosiJaKk(
  ageYear: number,
  ageMonth: number,
  birthYear: number,
  birthMonth: number,
): ElakeVuosiJaKkResult {
  let poikkeusIndeksointi = false;

  if (ageMonth === 0) {
    ageYear = ageYear - 1;
    ageMonth = 12;
  }

  let elakeKuukausi = birthMonth + ageMonth + 1;

  while (elakeKuukausi > 12) {
    elakeKuukausi = elakeKuukausi - 12;
  }

  let elakeVuosi: number;

  if (birthMonth + ageMonth >= 12) {
    elakeVuosi = birthYear + ageYear + 1;

    //näillä henkilöillä laskenta yms. toimii oikein eläkevuodella, indeksointi pitää ulottaa yhden vuoden pidemmälle ja lisätä eläkevuoteen lopuksi yksi
    /* Korjaus 24.5.2023 TYEL-395 */
    if (birthMonth + ageMonth >= 24) {
      poikkeusIndeksointi = true;
    }
  } else {
    elakeVuosi = birthYear + ageYear;
  }

  return { elakeVuosi, elakeKuukausi, poikkeusIndeksointi };
}

function vertaaIkia(ika1: number, kk1: number, ika2: number, kk2: number) {
  const summaIka1 = ika1 * 12 + kk1;
  const summaIka2 = ika2 * 12 + kk2;

  return summaIka1 > summaIka2;
}

function karttumaprosenttiPoikkeukset(
  birthYear: number,
  birthMonth: number,
  pensionAgeYear: number,
  pensionAgeMonth: number,
  vuosi: number,
) {
  /* Karttumaprosentin siirtymäsäännös, sekä karttuma vuosilta joilla henkilö siirtyy ja siirtyy pois
      siirtymäsäännön piiristä */
  const ageInYears = vuosi - birthYear;
  let karttumaprosentti;
  if (vuosi >= 2017 && vuosi <= 2025 && ageInYears >= 54 && ageInYears <= 62) {
    karttumaprosentti = 0.017;
  } else if (vuosi >= 2017 && vuosi <= 2025 && ageInYears === 53) {
    karttumaprosentti =
      (birthMonth / 12) * 0.015 + ((12 - birthMonth) / 12) * 0.017;
  } else if (
    vuosi >= 2017 &&
    vuosi <= 2025 &&
    ageInYears === 63 &&
    (pensionAgeYear != 63 || birthMonth + pensionAgeMonth > 12)
  ) {
    karttumaprosentti =
      (birthMonth / 12) * 0.017 + ((12 - birthMonth) / 12) * 0.015;
  } else if (
    vuosi >= 2017 &&
    vuosi <= 2025 &&
    ageInYears === 63 &&
    pensionAgeYear === 63
  ) {
    karttumaprosentti =
      (birthMonth / (birthMonth + pensionAgeMonth)) * 0.017 +
      (pensionAgeMonth / (pensionAgeMonth + birthMonth)) * 0.015;
  } else {
    karttumaprosentti = 0.015;
  }

  return karttumaprosentti;
}

function karttumaKuukaudet(
  laskentaVuosi: number,
  elakeVuosi: number,
  oveAgeYear: number,
  oveAgeMonth: number,
  sv: number,
  sk: number,
  ika: number,
  alkuVuosi: number,
  laskentaKuukausi: number,
) {
  // 17.5.2023 TYEL-395
  let karttumakuukaudet;
  if (laskentaVuosi != elakeVuosi) {
    karttumakuukaudet = 12;
  } else {
    //Huom. vanhuuseläkeiän täyttämiskuukautena tehdään vielä töitä
    if (oveAgeYear === ika) {
      karttumakuukaudet = oveAgeMonth + sk;
    } else {
      karttumakuukaudet = oveAgeMonth - (12 - sk);
    }
  }

  /* Jos eläkkeen karttumisen yläikäraja ylitetty, ei uusia karttumia */
  let karttumaraja = 70;

  if (sv < 1958) {
    karttumaraja = 68;
  } else if (sv >= 1958 && sv <= 1961) {
    karttumaraja = 69;
  }

  if (ika === karttumaraja) {
    karttumakuukaudet = Math.min(karttumakuukaudet, sk);
  } else if (ika > karttumaraja) {
    karttumakuukaudet = 0;
  }

  /* 17.5.2023 TYEL-395 alkuvuoden toteutuneet karttumat ovat jo syötetyssä karttumassa kuukausitason laskennassa */
  if (laskentaVuosi === alkuVuosi && laskentaKuukausi < 12) {
    karttumakuukaudet = karttumakuukaudet - laskentaKuukausi;
  }
  if (karttumakuukaudet < 0) {
    karttumakuukaudet = 0;
  }

  return karttumakuukaudet;
}

function lykkaysKorotusKuukaudet(
  sv: number,
  sk: number,
  pensionYear: number,
  pensionMonth: number,
  lowestPensionAgeYear: number,
  lowestPensionAgeMonth: number,
) {
  let lykkayskorotus;
  if (sv < 1954) {
    lykkayskorotus =
      (pensionYear - lowestPensionAgeYear - 2) * 12 + (pensionMonth + sk);
  } else {
    lykkayskorotus =
      (pensionYear - lowestPensionAgeYear) * 12 +
      (pensionMonth - lowestPensionAgeMonth);
  }

  if (lykkayskorotus < 0) {
    lykkayskorotus = 0;
  }

  return lykkayskorotus;
}

interface alinVeAndElinAikakerroin {
  lowestPensionAgeYear: number;
  lowestPensionAgeMonth: number;
  ek: number;
  ekFormatted: number;
}

function haeAlinVanhuuselakeIkaJaElinaikakerroin(
  sv: number,
  csvData: PensionEstimate[] | undefined,
): alinVeAndElinAikakerroin {
  if (!csvData) {
    return {
      lowestPensionAgeYear: 0,
      lowestPensionAgeMonth: 0,
      ek: 0.0,
      ekFormatted: 0.0,
    };
  }

  const lowestPensionAgeYear = pensionEstimateValue(sv, "alvev", csvData) || 0;
  const lowestPensionAgeMonth =
    pensionEstimateValue(sv, "alvekk", csvData) || 0;
  const ek = pensionEstimateValue(sv, "ek", csvData) || 0.0;
  const ekFormatted = pensionEstimateValue(sv, "EAK", csvData) || 0.0;

  return { lowestPensionAgeYear, lowestPensionAgeMonth, ek, ekFormatted };
}

function tarkistaValittuIka(
  valittuv: number,
  valittukk: number,
  lowestPensionAgeYear: number,
  lowestPensionAgeMonth: number,
): boolean {
  return (
    12 * valittuv + valittukk >=
    12 * lowestPensionAgeYear + lowestPensionAgeMonth
  );
}
